import OperationalContextScreen from "./routes/operational-context";
import { useEffect, useState } from "react";
import {
  administrationAreaIds,
  administrationPageDefinitions,
  deriveVisibleAdministrationAreas,
  deriveVisibleAdministrationPages,
  type AdministrationAreaId,
  type AdministrationPageId,
} from "@reminder-tracker/domain";
import { CatalogueAdministrationPanel } from "./features/catalogue/panel";
import { AdministrationMobileHeader, AdministrationNavigation } from "./administration-navigation";
import { useAdministratorAuthentication } from "./auth/authentication-context";
import { AdministratorMfaGate } from "./features/administrator-mfa/gate";
import { AdministratorMfaErrorBoundary } from "./features/administrator-mfa/error-boundary";
import {
  OperationalContextErrorBoundary,
  ProtectedOperationalContextScreen,
} from "./features/operational-context/protected-screen";
import { SupportCasesPanel } from "./features/support-cases/panel";
import { SupportPerformancePanel } from "./features/support-cases/performance";
import { RestrictedSupportCasesPanel } from "./features/support-cases/restricted";

export function App(): React.JSX.Element {
  const [requestedRoute, setRequestedRoute] = useState(readRequestedRoute);
  const [navigationOpen, setNavigationOpen] = useState(false);
  useEffect(() => {
    const navigate = () => {
      setRequestedRoute(readRequestedRoute());
      setNavigationOpen(false);
    };
    window.addEventListener("hashchange", navigate);
    return () => window.removeEventListener("hashchange", navigate);
  }, []);
  const { authentication, signIn, signOut } = useAdministratorAuthentication();
  const connected = authentication.state !== "disconnected";
  const permissions = authentication.state === "authorized" ? authentication.permissions : [];
  const visibleAreas = deriveVisibleAdministrationAreas(permissions);
  const section = visibleAreas.some((area) => area.id === requestedRoute.area)
    ? requestedRoute.area
    : (visibleAreas[0]?.id ?? "operations");
  const visiblePages = deriveVisibleAdministrationPages(section, permissions);
  const page = visiblePages.some((item) => item.id === requestedRoute.page)
    ? requestedRoute.page
    : (visiblePages[0]?.id ?? "overview");
  let content: React.JSX.Element;
  if (authentication.state === "initializing") {
    content = (
      <AccessMessage title="Securing Site Administration" copy="Checking the WorkOS session…" />
    );
  } else if (authentication.state === "anonymous") {
    content = (
      <AccessMessage
        title="Administrator sign-in"
        copy="Sign in through the approved WorkOS administration organization."
        actionLabel="Sign in securely"
        action={signIn}
      />
    );
  } else if (authentication.state === "denied") {
    content = (
      <AccessMessage
        title="Administration access denied"
        copy={authentication.reason}
        actionLabel="Sign out"
        action={signOut}
      />
    );
  } else if (authentication.state === "authorized") {
    content = (
      <AdministratorMfaErrorBoundary key={`${section}/${page}`} onReauthenticate={signOut}>
        <AdministratorMfaGate scope={section === "catalogue" ? "catalogue" : "operations"}>
          <OperationalContextErrorBoundary>
            {section === "catalogue" ? (
              <CatalogueAdministrationPanel page={page} />
            ) : section === "support" ? (
              page === "support-performance" ? (
                <SupportPerformancePanel />
              ) : page === "restricted-cases" ? (
                <RestrictedSupportCasesPanel />
              ) : (
                <SupportCasesPanel
                  page={page}
                  canLead={permissions.includes("support:lead")}
                  canWorkCases={permissions.includes("support:casework")}
                />
              )
            ) : (
              <ProtectedOperationalContextScreen page={page} />
            )}
          </OperationalContextErrorBoundary>
        </AdministratorMfaGate>
      </AdministratorMfaErrorBoundary>
    );
  } else {
    content = <OperationalContextScreen />;
  }
  return (
    <div className="app">
      <div className="env">
        <strong>
          {connected ? "Shared Test Site · synthetic data only" : "Local development"}
        </strong>
        <span>
          {connected
            ? "Customer reminder details remain private"
            : "Synthetic evidence only · external services are disconnected"}
        </span>
      </div>
      <div className="shell">
        <aside className="rail" data-open={navigationOpen} id="administration-navigation">
          <h1 className="brand">Reminder Tracker</h1>
          <p className="admin-label">Site Administration</p>
          {authentication.state === "authorized" ? (
            <AdministrationNavigation
              activeArea={section}
              activePage={page}
              permissions={permissions}
            />
          ) : null}
          <p className="rail-note">
            Site Administration is separate from household reminder content. Operational views use
            aggregate or deliberately masked evidence.
          </p>
        </aside>
        <main
          className="main"
          id={section === "operations" ? "operations" : "administration-content"}
        >
          {authentication.state === "authorized" ? (
            <AdministrationMobileHeader
              open={navigationOpen}
              onToggle={() => setNavigationOpen((open) => !open)}
            />
          ) : null}
          {content}
        </main>
      </div>
    </div>
  );
}

function readRequestedRoute(): Readonly<{
  area: AdministrationAreaId;
  page: AdministrationPageId;
}> {
  const [areaValue, pageValue] = window.location.hash.slice(1).split("/");
  const area = administrationAreaIds.includes(areaValue as AdministrationAreaId)
    ? (areaValue as AdministrationAreaId)
    : "operations";
  const firstPage = administrationPageDefinitions.find(
    (candidate) => candidate.areaId === area,
  )?.id;
  const page = administrationPageDefinitions.some(
    (candidate) => candidate.areaId === area && candidate.id === pageValue,
  )
    ? (pageValue as AdministrationPageId)
    : (firstPage ?? "overview");
  return { area, page };
}

function AccessMessage({
  title,
  copy,
  actionLabel,
  action,
}: Readonly<{
  title: string;
  copy: string;
  actionLabel?: string;
  action?: (() => Promise<void>) | null;
}>): React.JSX.Element {
  return (
    <section className="access-message" aria-live="polite">
      <h1>{title}</h1>
      <p>{copy}</p>
      {actionLabel !== undefined && action != null ? (
        <button type="button" onClick={() => void action()}>
          {actionLabel}
        </button>
      ) : null}
    </section>
  );
}
